import { TFunction } from 'i18next';
import React, { useCallback, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  DroneHarmonyGeodataProcessType,
  GeodataBoundaryPolygonModel,
  GroundControlPoint,
} from '../../../../../../../../../typings/api/skymap/rest/v1/.common';
import { TokenService } from '../../../../../../../services/authentication/token.service';
import {
  FileUpload,
  UploadManuallyStoppedError,
} from '../../../../../../../services/network/file-upload';
import { WebApi } from '../../../../../../../services/web-api';
import { FeatureFlagsManager } from '../../../../../../../utils/core/feature-flags-manager';
import { useDialog } from '../../../../../../hooks/use-dialog';
import { useErrorHandling } from '../../../../../../hooks/use-error-handling';
import { ProjectContext } from '../../../../../../state/project-state/project-state';
import { Button } from '../../../../../button/button';
import {
  BoundaryTypeIcon,
  StatusIcon,
} from '../../../../../cloud-processing/boundary-polygon-icons';
import { supportedBoundaryPolygonFileTypes } from '../../../../../cloud-processing/gcp-page/boundary-polygon-list';
import { GcpRow } from '../../../../../cloud-processing/gcp-parser';
import { GcpFileFormElement } from '../../../../../cloud-processing/geodata-configuration/gcp-file-form-element';
import { ConfirmationDialog } from '../../../../../confirmation-dialog/confirmation-dialog';
import { Dialog } from '../../../../../dialog/dialog';
import { FileSelectionButton } from '../../../../../file-selection-button/file-selection-button';
import { Icon } from '../../../../../icon/icon';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { Select } from '../../../../../select/select';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { useRemoveFlightPlanQuery } from '../../hooks';
import { ProcessingPackagesInfo } from '../add-flight-plan-dialog/processing-packages-info';
import { TerrainModelInfo } from '../add-flight-plan-dialog/terrain-model-info';
import { BoundaryPolygonPreview } from './boundary-polygon-preview';
import { useUpdateQuery } from './use-update-query';

export type FormValues = {
  gcpFilename: string | undefined | null;
  generateTerrainModel: boolean;
  geodataProcessType: DroneHarmonyGeodataProcessType;
};

export type FlightPlan = {
  id: string;
  guid: string;
  processingPackage: DroneHarmonyGeodataProcessType;
  generateTerrainModel: boolean;
  gcpAvailable: boolean;
  groundControlPoints?: GroundControlPoint[];
  boundaryPolygons?: GeodataBoundaryPolygonModel[];
};

const getDataProcessTypes = (t: TFunction) => {
  const values: { name: string; id: DroneHarmonyGeodataProcessType }[] = [
    {
      id: 'ORTHO_ONLY',
      name: t('setupDialog.cards.orthophoto.title', { ns: 'cloudProcessing' }),
    },
    {
      id: 'FULL',
      name: t('setupDialog.cards.standard.title', { ns: 'cloudProcessing' }),
    },
    {
      id: 'POINTCLOUD_ONLY',
      name: t('setupDialog.cards.pointCloud.title', { ns: 'cloudProcessing' }),
    },
    {
      id: 'FULL_HIGH_QUALITY',
      name: t('setupDialog.cards.premium.title', { ns: 'cloudProcessing' }),
    },
  ];

  if (FeatureFlagsManager.instance.isEnabled('SK-6613-signature-processing-package')) {
    values.push({
      id: 'SIGNATURE',
      name: t('setupDialog.cards.signature.title', { ns: 'cloudProcessing' }),
    });
  }

  if (FeatureFlagsManager.instance.isEnabled('SK-6920-pm')) {
    values.push({
      id: 'PM',
      name: 'WIP-2026',
    });
  }

  return values;
};

const getGenerateTerrainModelItems = (t: TFunction) => [
  {
    id: true,
    name: t('yes', { ns: 'common' }),
  },
  {
    id: false,
    name: t('no', { ns: 'common' }),
  },
];

/**
 * Converts GroundControlPoint[] from API to GcpRow[] format for the form.
 * Since these points are already validated by the API, we set them as valid_format.
 * Also checks for name duplications.
 */
const convertGroundControlPointsToGcpRows = (
  groundControlPoints: GroundControlPoint[] | undefined,
): GcpRow[] => {
  if (!groundControlPoints || groundControlPoints.length === 0) {
    return [];
  }

  const rows: GcpRow[] = groundControlPoints.map((gcp) => ({
    name: {
      status: gcp.name.trim() === '' ? 'invalid' : 'valid',
      value: gcp.name,
    },
    n: {
      status: 'valid_format',
      value: gcp.northing.toString(),
    },
    e: {
      status: 'valid_format',
      value: gcp.easting.toString(),
    },
    h: {
      status: 'valid_format',
      value: gcp.height.toString(),
    },
    included: true,
  }));

  // Check for name duplications (case insensitive)
  const nameCounts = new Map<string, number>();
  rows.forEach((row) => {
    const nameKey = row.name.value.trim().toLowerCase();
    nameCounts.set(nameKey, (nameCounts.get(nameKey) || 0) + 1);
  });

  rows.forEach((row) => {
    const nameKey = row.name.value.trim().toLowerCase();
    if (nameCounts.get(nameKey)! > 1 && row.name.status === 'valid') {
      row.name.status = 'duplication';
    }
  });

  return rows;
};

type UploadedBoundaryFile = {
  id: string;
  fileName: string;
  uploadId?: string;
  isUploading: boolean;
};

type BoundaryFileItemProps = {
  fileName: string;
  isUploading: boolean;
  onRemove: () => void;
};

const BoundaryFileItem = ({ fileName, isUploading, onRemove }: BoundaryFileItemProps) => {
  const { t } = useTranslation();
  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between">
      <FileName>{fileName}</FileName>
      <Icon
        fixedWidth={true}
        icon={['fal', isUploading ? 'spinner' : 'trash-can']}
        size="sm"
        spin={isUploading}
        style={{ cursor: 'pointer' }}
        title={isUploading ? t('uploading', { ns: 'common' }) : t('delete', { ns: 'common' })}
        onClick={onRemove}
      />
    </Stack>
  );
};

type Props = {
  flightPlan: FlightPlan;
  onClose: () => void;
};

const EditFlightPlanDialog = (props: Props) => {
  const { t } = useTranslation();
  const generateTerrainModelItemsRef = useRef(getGenerateTerrainModelItems(t));
  const geodataProcessTypesRef = useRef(getDataProcessTypes(t));
  const { project } = React.useContext(ProjectContext);
  const gcpPointsRef = React.useRef<GcpRow[]>(
    convertGroundControlPointsToGcpRows(props.flightPlan.groundControlPoints),
  );
  const { handleError } = useErrorHandling();
  const deleteDialog = useDialog();
  const [uploadedBoundaryFiles, setUploadedBoundaryFiles] = useState<UploadedBoundaryFile[]>([]);
  const [modifiedBoundaryPolygons, setModifiedBoundaryPolygons] = useState<
    Map<string, { enabled?: boolean; boundaryType?: 'Inner' | 'Outer' }>
  >(new Map());

  // Initialize GCP points when flight plan changes
  React.useEffect(() => {
    gcpPointsRef.current = convertGroundControlPointsToGcpRows(
      props.flightPlan.groundControlPoints,
    );
  }, [props.flightPlan.groundControlPoints]);

  // Get the current state of boundary polygons (original + modifications)
  const getBoundaryPolygonState = useCallback(
    (bp: GeodataBoundaryPolygonModel) => {
      const modifications = modifiedBoundaryPolygons.get(bp.id);
      return {
        ...bp,
        enabled: modifications?.enabled !== undefined ? modifications.enabled : bp.enabled,
        boundaryType:
          modifications?.boundaryType !== undefined ? modifications.boundaryType : bp.boundaryType,
      };
    },
    [modifiedBoundaryPolygons],
  );

  // Get all boundary polygons with modifications applied
  const boundaryPolygonsWithModifications = React.useMemo(() => {
    return (props.flightPlan.boundaryPolygons ?? []).map(getBoundaryPolygonState);
  }, [props.flightPlan.boundaryPolygons, getBoundaryPolygonState]);

  const { updateQuery, getErrorForPath, errorList } = useUpdateQuery({
    gcpFileContent: gcpPointsRef.current,
    projectId: project.id,
    flightPlanId: props.flightPlan.id,
    boundaryPolygonUploadIds: uploadedBoundaryFiles
      .filter((f) => f.uploadId)
      .map((f) => f.uploadId!),
    existingBoundaryPolygons: boundaryPolygonsWithModifications,
    onSuccess: props.onClose,
  });

  const form = useForm<FormValues>({
    defaultValues: {
      geodataProcessType: props.flightPlan.processingPackage,
      gcpFilename: props.flightPlan.gcpAvailable ? 'GCP.txt' : undefined,
      generateTerrainModel: props.flightPlan.generateTerrainModel,
    },
  });

  const { clearErrors, control, formState, handleSubmit, setError, trigger, watch } = form;

  const removeFlightPlanMutation = useRemoveFlightPlanQuery({
    projectId: project.id,
    flightPlanId: props.flightPlan.id,
    onError: (err) => {
      handleError(err);
    },
    onMutate: () => {
      props.onClose();
    },
  });

  const onBoundaryFilesSelected = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const fileKey = `${file.name}_${file.size}_${file.lastModified}`;

        // Add file to the list immediately with uploading state
        setUploadedBoundaryFiles((prev) => [
          ...prev,
          {
            id: fileKey,
            fileName: file.name,
            isUploading: true,
          },
        ]);

        try {
          // Upload the file
          const upload = new FileUpload({
            apiBaseUrl: WebApi.getUrl(),
            authToken: new TokenService().get(),
            file: file,
            concurrency: 1,
          });

          const { id: uploadId } = await upload.start();

          // Update the file with upload ID and mark as completed
          setUploadedBoundaryFiles((prev) =>
            prev.map((f) => (f.id === fileKey ? { ...f, uploadId, isUploading: false } : f)),
          );
        } catch (error) {
          console.error('Failed to upload boundary polygon file:', error);
          if (error instanceof UploadManuallyStoppedError) {
            // Remove the file from the list if upload was manually stopped
            setUploadedBoundaryFiles((prev) => prev.filter((f) => f.id !== fileKey));
            return;
          }
          handleError(error);
          // Remove the file from the list on error
          setUploadedBoundaryFiles((prev) => prev.filter((f) => f.id !== fileKey));
        }
      }
    },
    [handleError],
  );

  const removeBoundaryFile = useCallback((fileId: string) => {
    setUploadedBoundaryFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  const handleToggleEnabled = useCallback(
    (id: string) => {
      setModifiedBoundaryPolygons((prev) => {
        const newMap = new Map(prev);
        const current = props.flightPlan.boundaryPolygons?.find((bp) => bp.id === id);
        if (current) {
          const existingMod = newMap.get(id);
          const currentEnabled =
            existingMod?.enabled !== undefined ? existingMod.enabled : current.enabled;
          newMap.set(id, {
            ...existingMod,
            enabled: !currentEnabled,
          });
        }
        return newMap;
      });
    },
    [props.flightPlan.boundaryPolygons],
  );

  const handleToggleBoundaryType = useCallback(
    (id: string) => {
      setModifiedBoundaryPolygons((prev) => {
        const newMap = new Map(prev);
        const current = props.flightPlan.boundaryPolygons?.find((bp) => bp.id === id);
        if (current) {
          const existingMod = newMap.get(id);
          const currentBoundaryType =
            existingMod?.boundaryType !== undefined
              ? existingMod.boundaryType
              : current.boundaryType;
          newMap.set(id, {
            ...existingMod,
            boundaryType: currentBoundaryType === 'Inner' ? 'Outer' : 'Inner',
          });
        }
        return newMap;
      });
    },
    [props.flightPlan.boundaryPolygons],
  );

  React.useEffect(() => {
    const gcpError = getErrorForPath('body.groundControlPoints');
    if (gcpError) {
      setError('gcpFilename', {
        type: 'custom',
        message: gcpError,
      });
    } else {
      clearErrors('gcpFilename');
    }
    const packageError = getErrorForPath('body.package');
    if (packageError) {
      setError('generateTerrainModel', {
        type: 'custom',
        message: packageError,
      });
      setError('geodataProcessType', {
        type: 'custom',
        message: packageError,
      });
    } else {
      clearErrors('generateTerrainModel');
      clearErrors('geodataProcessType');
    }
  }, [clearErrors, getErrorForPath, setError]);

  return (
    <>
      <Dialog height={'expand'} width={500} onClose={props.onClose}>
        {{
          header: t('editFlightPlanDialog.title', { ns: 'cloudProcessing' }),
          content: (
            <form
              id="editFlightPlanForm"
              onSubmit={handleSubmit((form) => updateQuery.mutate(form))}
            >
              <Stack spacing={1}>
                <LabelledContainer text={t('addFlightPlanDialog.id', { ns: 'cloudProcessing' })}>
                  {(formElementId) => (
                    <TextBox disabled={true} id={formElementId()} value={props.flightPlan.guid} />
                  )}
                </LabelledContainer>

                <LabelledContainer
                  infoTooltip={
                    <ProcessingPackagesInfo geodataProcessType={watch('geodataProcessType')} />
                  }
                  required={true}
                  text={t('setupDialog.cards.processingPackage', { ns: 'cloudProcessing' })}
                >
                  {(formElementId) => {
                    const id = formElementId();

                    return (
                      <Controller
                        control={control}
                        defaultValue={props.flightPlan.processingPackage}
                        name="geodataProcessType"
                        render={({ field }) => (
                          <Select
                            {...field}
                            disabled={updateQuery.isPending}
                            errorMessage={formState.errors.geodataProcessType?.message}
                            id={id}
                            options={geodataProcessTypesRef.current}
                            placeholderText={t('notSelected', { ns: 'common' })}
                          />
                        )}
                        rules={{
                          required: true,
                        }}
                      />
                    );
                  }}
                </LabelledContainer>

                <LabelledContainer
                  infoTooltip={<TerrainModelInfo />}
                  required={true}
                  text={t('setupDialog.addOns.terrainModel.generateTerrainModel', {
                    ns: 'cloudProcessing',
                  })}
                >
                  {(formElementId) => {
                    const id = formElementId();

                    return (
                      <Controller
                        control={control}
                        name="generateTerrainModel"
                        render={({ field }) => (
                          <Select
                            {...field}
                            disabled={
                              updateQuery.isPending || watch('geodataProcessType') === 'ORTHO_ONLY'
                            }
                            errorMessage={formState.errors.generateTerrainModel?.message}
                            id={id}
                            options={generateTerrainModelItemsRef.current}
                            onChange={(e) => {
                              field.onChange(e.target.value === 'true');
                              void trigger('generateTerrainModel');
                            }}
                          />
                        )}
                      />
                    );
                  }}
                </LabelledContainer>

                <GcpFileFormElement
                  disabled={updateQuery.isPending}
                  form={form}
                  gcpPointsRef={gcpPointsRef}
                  required={false}
                />

                {boundaryPolygonsWithModifications.length > 0 && (
                  <LabelledContainer
                    text={t('boundaryPolygonList.title', { ns: 'cloudProcessing' })}
                  >
                    <Stack spacing={0.5}>
                      {boundaryPolygonsWithModifications.length > 0 && (
                        <Stack spacing={0.5}>
                          {boundaryPolygonsWithModifications.map((boundaryPolygon) => (
                            <Stack
                              alignItems="center"
                              direction="row"
                              key={boundaryPolygon.id}
                              spacing={0.5}
                            >
                              <StatusIcon
                                model={boundaryPolygon}
                                t={t}
                                onToggleEnabled={handleToggleEnabled}
                              />
                              <BoundaryTypeIcon
                                model={boundaryPolygon}
                                t={t}
                                onToggleBoundaryType={handleToggleBoundaryType}
                              />
                              <span>{boundaryPolygon.fileName}</span>
                            </Stack>
                          ))}
                          <BoundaryPolygonPreview
                            boundaryPolygons={boundaryPolygonsWithModifications}
                          />
                        </Stack>
                      )}

                      <FileSelectionButton
                        accept={supportedBoundaryPolygonFileTypes}
                        disabled={updateQuery.isPending}
                        leftIcon={{ icon: ['fad', 'file-upload'] }}
                        multiple={true}
                        text={t('boundaryPolygonList.addBoundaryPolygons', {
                          ns: 'cloudProcessing',
                        })}
                        variant="contained"
                        onFilesSelected={onBoundaryFilesSelected}
                      />

                      {uploadedBoundaryFiles.length > 0 && (
                        <Stack spacing={0.5}>
                          {uploadedBoundaryFiles.map((file) => (
                            <BoundaryFileItem
                              fileName={file.fileName}
                              isUploading={file.isUploading}
                              key={file.id}
                              onRemove={() => removeBoundaryFile(file.id)}
                            />
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </LabelledContainer>
                )}

                {errorList}
              </Stack>
            </form>
          ),
          footer: {
            left: (
              <>
                <Button
                  color="error"
                  disabled={removeFlightPlanMutation.isPending}
                  variant="contained"
                  onClick={() => deleteDialog.show()}
                >
                  {t('delete', { ns: 'common' })}
                </Button>
              </>
            ),
            right: (
              <>
                <Button
                  color="primary"
                  disabled={
                    updateQuery.isPending ||
                    removeFlightPlanMutation.isPending ||
                    uploadedBoundaryFiles.some((f) => f.isUploading)
                  }
                  form="editFlightPlanForm"
                  type="submit"
                  variant="contained"
                >
                  {t('save', { ns: 'common' })}
                </Button>
                <Button
                  disabled={updateQuery.isPending || removeFlightPlanMutation.isPending}
                  variant="text"
                  onClick={props.onClose}
                >
                  {t('close', { ns: 'common' })}
                </Button>
              </>
            ),
          },
        }}
      </Dialog>

      {deleteDialog.render((pos) => (
        <ConfirmationDialog
          confirmationText={t('editFlightPlanDialog.removeFlightPlanConfirmationTitle', {
            ns: 'cloudProcessing',
          })}
          negative={true}
          pos={pos}
          title={t('editFlightPlanDialog.removeFlightPlan', { ns: 'cloudProcessing' })}
          onCancel={deleteDialog.hide}
          onConfirm={async () => {
            removeFlightPlanMutation.mutate();
            deleteDialog.hide();
            return Promise.resolve();
          }}
        >
          <Stack direction="column" spacing={1}>
            <span>
              {t('editFlightPlanDialog.removeFlightPlanConfirmationText', {
                ns: 'cloudProcessing',
              })}
            </span>
            <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
              {t('editFlightPlanDialog.notPossibleToRevert', { ns: 'cloudProcessing' })}
            </InfoBox>
          </Stack>
        </ConfirmationDialog>
      ))}
    </>
  );
};

const FileName = styled.span`
  font-size: 0.9em;
  color: ${({ theme }) => theme.color.gray.darkest};
  flex: 1;
  margin-right: 0.5em;
`;

export { EditFlightPlanDialog };
